import React from 'react';
import classNames from 'classnames';
import type { SxProps, Theme } from '@mui/material/styles';

import Icons from 'assets/icons';

import { schema } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-configuration/goals-configuration';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import {
  formatNumberWithSuffix,
  formatWithThousandSeparator,
} from 'helpers/formatted-number.helpers';

import { TYPES_WITHOUT_SUFFIXES } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-tab.constants';
import { getTestId } from 'helpers/test-helpers';

import type {
  Goal,
  GoalDetail,
} from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface RelativeIcons {
  Increase: React.JSX.Element;
  Decrease: React.JSX.Element;
  Maintain: React.JSX.Element;
}

const relativeToLastYearSymbols: RelativeIcons = {
  Increase: <Icons.ArrowUp />,
  Decrease: <Icons.ArrowDown />,
  Maintain: <Icons.Maintain />,
};

const RELATIVE_TO_LAST_YEAR_SX = {
  Increase: { color: 'tokens.success.700' },
  Decrease: { color: 'tokens.critical.600' },
  Maintain: { color: 'tokens.gray.600' },
} satisfies Record<string, SxProps<Theme>>;

const getRelativeToLastYearSx = (
  relativeToLastYear: string | undefined
): SxProps<Theme> | undefined =>
  RELATIVE_TO_LAST_YEAR_SX[
    (relativeToLastYear ?? '') as keyof typeof RELATIVE_TO_LAST_YEAR_SX
  ];

const subFieldsClasses =
  'bg-gray-100 group-aria-selected:bg-navy-100 py-1 px-1.5 rounded';

const getOptionsText = (options: string[]): string => options.join(', ');

interface MatrixGoal extends Goal {
  relativeToLastYear?: keyof RelativeIcons | string;
  value?: number | string;
  subType?: string | null;
  funnelMetric?: string | null;
  name?: string;
}

const renderIcon = (goal: MatrixGoal): React.JSX.Element => (
  <Box
    component="span"
    role="img"
    aria-label={goal.relativeToLastYear}
    sx={getRelativeToLastYearSx(goal.relativeToLastYear)}
    data-testid={getTestId({
      prefix: 'goal-relative-to-last-year',
      label: goal.relativeToLastYear ?? '',
      suffix: 'icon',
    })}
  >
    {relativeToLastYearSymbols[goal.relativeToLastYear as keyof RelativeIcons]}
  </Box>
);

const renderSubType = (goal: MatrixGoal): React.JSX.Element | null => {
  if (!goal.subType) return null;

  return (
    <span className={subFieldsClasses} data-testid="goal-sub-type">
      {goal.subType}
    </span>
  );
};

const renderFunnelMetric = (goal: MatrixGoal): React.JSX.Element | null => {
  if (!goal.funnelMetric) return null;

  return (
    <span className={subFieldsClasses} data-testid="goal-funnel-metric">
      {goal.funnelMetric}
    </span>
  );
};

const renderSubArea = (goalDetails: GoalDetail): React.JSX.Element | null => {
  if (!goalDetails.subArea) return null;

  return (
    <span className={subFieldsClasses} data-testid="goal-sub-area">
      {goalDetails.subArea}
    </span>
  );
};

const getRuleContent = (
  rule: GoalDetail,
  goal: MatrixGoal
): string | undefined => {
  const categoryContentMap: Record<string, string | undefined> = {
    Topline: rule.focusArea,
    Other: goal.name,
  };

  const key = goal.category === 'Combined' ? rule.category : goal.category;
  const matched = key ? categoryContentMap[key] : undefined;

  if (matched !== undefined) return matched;

  return getOptionsText(
    ([] as string[]).concat((rule.options as string | string[]) ?? [])
  );
};

const renderDetails = (
  rule: GoalDetail,
  goal: MatrixGoal
): React.JSX.Element => (
  <span className="break-words">{getRuleContent(rule, goal)}</span>
);

interface RenderValueParams {
  value: number | string | undefined;
  valueType: string | undefined;
  goalType: string | undefined;
}

export const renderValue = ({
  value,
  valueType,
  goalType,
}: RenderValueParams): string => {
  const TYPES: Record<
    string,
    { prefix: string | null; suffix: string | null }
  > = {
    PERCENT: { prefix: null, suffix: '%' },
    DOLLAR: { prefix: '$', suffix: null },
    NUMBER: { prefix: null, suffix: null },
  };

  const valuePrefix = (valueType && TYPES[valueType]?.prefix) || '';
  const valueSuffix = (valueType && TYPES[valueType]?.suffix) || '';

  const parsedValue =
    goalType && TYPES_WITHOUT_SUFFIXES.includes(goalType)
      ? formatWithThousandSeparator(value ?? '')
      : formatNumberWithSuffix(Number(value));

  return `${valuePrefix}${parsedValue}${valueSuffix}`;
};

export const getValueType = (goal: MatrixGoal): string | undefined => {
  if (!goal.type) return undefined;

  const valueType =
    schema[goal.type]?.subType?.find((sub) => sub.value === goal.subType)
      ?.valueType || schema[goal.type]?.valueType;

  return valueType;
};

export const renderGoal = (goal: MatrixGoal): React.JSX.Element => {
  const valueType = getValueType(goal);
  const details = goal.details ?? [];

  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
      <Stack
        direction="row"
        sx={{
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          overflowWrap: 'break-word',
        }}
      >
        {goal.category !== 'Other' && (
          <>
            <Box
              component="span"
              sx={getRelativeToLastYearSx(goal.relativeToLastYear)}
              data-testid="goal-value"
            >
              {renderValue({
                value: goal.value,
                valueType,
                goalType: goal.type,
              })}
            </Box>
            {renderIcon(goal)}
            {renderFunnelMetric(goal)}
            {renderSubType(goal)}
          </>
        )}

        {details.map((item, detailIndex) => (
          <React.Fragment key={(item as { id?: string }).id ?? detailIndex}>
            {renderSubArea(item)}
            <Stack
              direction="row"
              sx={{ flexWrap: 'wrap', alignItems: 'center' }}
              data-testid="goal-rule-content"
            >
              {renderDetails(item, goal)}
            </Stack>
            {detailIndex !== details.length - 1 && (
              <Icons.PlusInCircle
                className="h-4 w-4 text-gray-600"
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Stack>
  );
};

const renderCellData = (goal: MatrixGoal): React.JSX.Element => (
  <Stack
    key={goal.id}
    direction="row"
    sx={{
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      fontWeight: 600,
      overflowWrap: 'break-word',
      color: 'tokens.gray.600',
    }}
  >
    <Stack direction="row" sx={{ alignItems: 'baseline' }}>
      {goal.category === 'Other' && <span className="mr-2">&bull;</span>}
      {renderGoal(goal)}
    </Stack>

    <Icons.EditPencil
      className="absolute top-2 right-3 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 text-lime-500 transition-all h-5 w-5"
      aria-label="Edit"
      data-testid="edit-goal-button"
    />
  </Stack>
);

interface GridMatrixCellClickArg {
  category: string;
  type: string;
  id?: string | number;
}

interface GridMatrixCellProps {
  cellData: MatrixGoal[];
  onClick: (arg: GridMatrixCellClickArg) => void;
  row: string;
  header: string;
}

const GridMatrixCell = ({
  cellData,
  onClick,
  row,
  header,
}: GridMatrixCellProps): React.JSX.Element => (
  <button
    className={classNames(
      'relative flex flex-col group p-2 pr-8 space-y-1 w-full h-full focus-visible:outline text-left',
      { 'justify-start': cellData.length, 'justify-center': !cellData.length }
    )}
    onClick={() =>
      cellData.length
        ? onClick(cellData[0] as unknown as GridMatrixCellClickArg)
        : onClick({ category: row, type: header })
    }
    data-testid={getTestId({
      prefix: 'add-goal',
      label: `${row} ${header}`,
      suffix: 'button',
    })}
  >
    {cellData.length ? (
      cellData.map((goal) => renderCellData(goal))
    ) : (
      <div className="opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 mx-auto transition-all sm:text-xs md:text-sm bg-lime-400 px-3 py-1.5  rounded-md flex items-center justify-center">
        <Icons.Plus className="mr-1 text-navy-800 h-3 w-3" aria-hidden="true" />
        <span className="text-sm font-semibold text-navy-800">Add a goal</span>
      </div>
    )}
  </button>
);

export default GridMatrixCell;
